import { env } from '@/config/env'
import type { QueuedEvent } from '@/lib/event-queue'

/**
 * Screen views by route pattern plus the funnel steps below, to two sinks:
 * - GA4 when `VITE_ANALYTICS_ID` is set (gtag loaded lazily), else the console in development;
 * - our own `POST /v2/events` (LP-947), only for names in the LP-938 catalog, wired by `src/lib/activity.ts`.
 * The "Share usage data" opt-out stops both.
 */
export type FunnelEvent =
  | 'otp_sent'
  | 'signed_in'
  | 'profile_completed'
  | 'property_created'
  | 'lease_created'
  | 'rent_payment_started'
  | 'payment_recorded'
  | 'agreement_drafted'
  | 'agreement_signing_initiated'
  | 'interest_shown'
  | 'maintenance_raised'
  | 'support_requested'
  | 'plan_change_started'
  | 'tenant_profile_saved'
  | 'role_switched'

type Props = Record<string, string | number | boolean | undefined>

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let loaded = false
function ensureGtag(): boolean {
  if (!env.analyticsId || typeof window === 'undefined') return false
  if (loaded) return true
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args)
  }
  window.gtag('js', new Date())
  window.gtag('config', env.analyticsId, { send_page_view: false, anonymize_ip: true })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(env.analyticsId)}`
  document.head.appendChild(script)
  loaded = true
  return true
}

/** The route pattern, not the URL: ids never leave the browser. */
export function screenName(pathname: string): string {
  return pathname.replace(/\/[0-9a-f]{24}(?=\/|$)/g, '/:id').replace(/\/(L|p|t|tp|sr|dk|ba)\d+(?=\/|$)/g, '/:id') || '/'
}

/** The first-party sink, set once at start-up so this module imports nothing that imports `http`. */
export interface FirstPartySink {
  /** False when the person turned off "Share usage data": nothing goes to GA4 or to us. */
  allowed: () => boolean
  push: (event: QueuedEvent) => void
  role: () => string | undefined
  /** The device id while signed out, so the backend can join a guest's events to the account after sign-in. */
  anonymousId: () => string | undefined
}

let sink: FirstPartySink | null = null
export function setFirstPartySink(next: FirstPartySink | null): void {
  sink = next
}

const allowed = () => sink?.allowed() ?? true

export function track(event: FunnelEvent | `screen_view`, props: Props = {}): void {
  if (!allowed()) return
  const payload = { ...props, app_env: env.appEnv, app_version: __APP_VERSION__ }
  if (env.isDev) console.debug('[analytics]', event, payload)
  if (ensureGtag()) window.gtag!('event', event, payload)
}

/** The v1 action names of the LP-938 catalog; anything else never reaches `/v2/events`. */
export type CatalogAction =
  | 'signup_started'
  | 'signup_completed'
  | 'property_create_started'
  | 'property_created'
  | 'lease_create_started'
  | 'lease_created'
  | 'agreement_started'
  | 'agreement_signed'
  | 'rent_pay_started'
  | 'rent_paid'
  | 'kyc_started'
  | 'kyc_completed'
  | 'maintenance_created'
  | 'listing_interest_sent'
  | 'share_invite'

/** Only the catalog's allowed keys; values are enums or ids, never text the person typed. */
type CatalogProps = Partial<Record<'propertyId' | 'leaseId' | 'agreementId' | 'method' | 'step' | 'source' | 'plan', string | number>>

function send(name: CatalogAction | 'screen_view', extra: { screen?: string; props?: CatalogProps } = {}): void {
  if (!sink || !sink.allowed()) return
  const role = sink.role()
  const props = { ...(role ? { role } : {}), ...extra.props }
  const anonymousId = sink.anonymousId()
  sink.push({ name, ts: new Date().toISOString(), ...(extra.screen ? { screen: extra.screen } : {}), ...(Object.keys(props).length ? { props } : {}), ...(anonymousId ? { anonymousId } : {}) })
}

/** A key action from a screen that has no single request to hang it on (KYC). */
export function trackAction(name: CatalogAction, props?: CatalogProps): void {
  send(name, { props })
}

/** Route pattern (from `screenName`) → the catalog's screen name. Unlisted routes are not sent to `/v2/events`. */
const CATALOG_SCREENS: Record<string, string> = {
  '/login': 'auth.login',
  '/verify': 'auth.otp',
  '/complete-profile': 'onboarding.profile',
  '/landlord/dashboard': 'dashboard',
  '/tenant/dashboard': 'dashboard',
  '/landlord/properties': 'properties.list',
  '/landlord/properties/:id': 'properties.detail',
  '/landlord/properties/new': 'properties.create',
  '/landlord/leases': 'leases.list',
  '/lease/:id': 'leases.detail',
  '/landlord/leases/new': 'leases.create',
  '/landlord/tenants': 'tenants.list',
  '/landlord/transactions': 'payments.list',
  '/tenant/transactions': 'payments.list',
  '/lease/:id/pay': 'payments.pay',
  '/lease/:id/agreement': 'agreements.detail',
  '/lease/:id/agreement/document': 'agreements.detail',
  '/landlord/maintenance': 'maintenance.list',
  '/tenant/browse': 'marketplace.browse',
  '/tenant/browse/:id': 'marketplace.detail',
  '/notifications': 'notifications.inbox',
  '/account': 'settings',
  '/account/sessions': 'settings.sessions',
  '/landlord/subscriptions': 'subscription.plans',
  '/account/kyc': 'kyc',
  '/account/support': 'help',
}

/** Entering these routes starts a catalog flow; they are the same points the app uses (LP-946). */
const SCREEN_ACTIONS: Record<string, CatalogAction> = {
  '/complete-profile': 'signup_started',
  '/landlord/properties/new': 'property_create_started',
  '/landlord/leases/new': 'lease_create_started',
  '/lease/:id/agreement/new': 'agreement_started',
  '/lease/:id/pay': 'rent_pay_started',
  '/landlord/transactions/new': 'rent_pay_started',
}

/** An id-shaped value for `props`: ids only, never anything a person typed. */
const idProp = (value: unknown): string | undefined => (typeof value === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(value) ? value : undefined)

const leaseIdIn = (pathname: string) => idProp(pathname.match(/^\/lease\/([^/]+)/)?.[1])

export function catalogScreen(pathname: string): string | null {
  return CATALOG_SCREENS[screenName(pathname)] ?? null
}

let lastScreen = ''
export function screenView(pathname: string): void {
  const screen = screenName(pathname)
  if (screen === lastScreen) return
  lastScreen = screen
  track('screen_view', { screen })
  const catalog = CATALOG_SCREENS[screen]
  if (catalog) send('screen_view', { screen: catalog })
  const action = SCREEN_ACTIONS[screen]
  if (action) {
    const leaseId = leaseIdIn(pathname)
    send(action, { props: leaseId ? { leaseId } : undefined })
  }
}

/** Tests only. */
export function resetScreenViewForTests(): void {
  lastScreen = ''
  paidTransactions.clear()
}

const FUNNEL: { method: string; path: RegExp; event: FunnelEvent }[] = [
  { method: 'POST', path: /^\/auth\/otp\/send$/, event: 'otp_sent' },
  { method: 'POST', path: /^\/auth\/otp\/verify$/, event: 'signed_in' },
  { method: 'POST', path: /^\/profile\/complete$/, event: 'profile_completed' },
  { method: 'POST', path: /^\/property$/, event: 'property_created' },
  { method: 'POST', path: /^\/leases$/, event: 'lease_created' },
  { method: 'POST', path: /^\/payments\/rent$/, event: 'rent_payment_started' },
  { method: 'POST', path: /^\/agreements\/draft$/, event: 'agreement_drafted' },
  { method: 'POST', path: /^\/agreement-sign$/, event: 'agreement_signing_initiated' },
  { method: 'POST', path: /^\/property\/[^/]+\/interest$/, event: 'interest_shown' },
  { method: 'POST', path: /^\/leases\/[^/]+\/maintenance$/, event: 'maintenance_raised' },
  { method: 'POST', path: /^\/support\/requests$/, event: 'support_requested' },
  { method: 'POST', path: /^\/v2\/subscription$/, event: 'plan_change_started' },
  { method: 'PUT', path: /^\/tenant-profile$/, event: 'tenant_profile_saved' },
]

const pathOf = (url: string) => url.replace(/^https?:\/\/[^/]+/, '').split('?')[0]

export function funnelEventFor(method: string | undefined, url: string | undefined): FunnelEvent | null {
  if (!method || !url) return null
  const path = pathOf(url)
  const m = method.toUpperCase()
  return FUNNEL.find((f) => f.method === m && f.path.test(path))?.event ?? null
}

interface ResponseContext {
  match: RegExpMatchArray
  body: Record<string, unknown>
  data: unknown
}

/**
 * Successful responses that are a catalog action, at the app's points (LP-946). `roles` limits a rule to one side;
 * `paidOnce` keys a transaction so a polled status counts once. `rent_paid` is a settled payment only: the tenant's
 * online payment, a landlord's record, or a landlord accepting a tenant's claim — a tenant's own cash or UPI claim is not.
 */
const CATALOG_RESPONSES: {
  method: string
  path: RegExp
  name: CatalogAction
  roles?: string[]
  when?: (c: ResponseContext) => boolean
  props?: (c: ResponseContext) => CatalogProps
  paidOnce?: (c: ResponseContext) => string
}[] = [
  { method: 'POST', path: /^\/profile\/complete$/, name: 'signup_completed' },
  { method: 'POST', path: /^\/property$/, name: 'property_created', props: (c) => ids({ propertyId: dataField(c, 'propertyId') }) },
  { method: 'POST', path: /^\/leases$/, name: 'lease_created', props: (c) => ids({ leaseId: dataField(c, 'leaseId') ?? dataField(c, 'id'), propertyId: c.body.propertyId }) },
  // A landlord's record is answered with the bare transaction id: paid there and then.
  { method: 'POST', path: /^\/payments\/rent$/, name: 'rent_paid', roles: ['landlord'], when: (c) => typeof c.data === 'string', props: (c) => ({ ...methodProp(c.body.group), ...ids({ leaseId: c.body.leaseId }) }), paidOnce: (c) => String(c.data) },
  { method: 'GET', path: /^\/payments\/([^/]+)\/status$/, name: 'rent_paid', roles: ['tenant'], when: (c) => (c.data as { status?: string } | null)?.status === 'completed', props: () => ({ method: 'online' }), paidOnce: (c) => c.match[1] },
  { method: 'GET', path: /^\/payments\/([^/]+)\/validate$/, name: 'rent_paid', roles: ['tenant'], when: (c) => (c.data as { confirmed?: boolean } | null)?.confirmed === true, props: () => ({ method: 'online' }), paidOnce: (c) => c.match[1] },
  { method: 'PATCH', path: /^\/payments\/([^/]+)\/accept$/, name: 'rent_paid', roles: ['landlord'], props: () => ({ source: 'claim_accepted' }), paidOnce: (c) => c.match[1] },
  { method: 'POST', path: /^\/property\/([^/]+)\/interest$/, name: 'listing_interest_sent', props: (c) => ids({ propertyId: c.match[1] }) },
  { method: 'POST', path: /^\/leases\/([^/]+)\/maintenance$/, name: 'maintenance_created', props: (c) => ids({ leaseId: c.match[1] }) },
  // KYC, per step: that step's submission succeeded. A witness's or legal landlord's document is not the person's own KYC.
  { method: 'POST', path: /^\/document-kyc\/verification$/, name: 'kyc_completed', when: (c) => (c.body.kycType ?? 'own-kyc') === 'own-kyc', props: () => ({ step: 'document' }) },
  { method: 'POST', path: /^\/banking\/account$/, name: 'kyc_completed', props: () => ({ step: 'bank' }) },
  { method: 'PUT', path: /^\/profile$/, name: 'kyc_completed', when: (c) => !!c.body.address, props: () => ({ step: 'address' }) },
]

const dataField = (c: ResponseContext, key: string): unknown => (c.data && typeof c.data === 'object' ? (c.data as Record<string, unknown>)[key] : undefined)

function ids(values: Partial<Record<'propertyId' | 'leaseId', unknown>>): CatalogProps {
  const out: CatalogProps = {}
  for (const [k, v] of Object.entries(values) as ['propertyId' | 'leaseId', unknown][]) {
    const id = idProp(v)
    if (id) out[k] = id
  }
  return out
}

const methodProp = (group: unknown): CatalogProps => (typeof group === 'string' && group.length <= 64 ? { method: group } : {})

const paidTransactions = new Set<string>()

function parseBody(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>
  if (typeof raw !== 'string') return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

/** Called by the http client for every successful response: the GA4 funnel step and any catalog action it is. */
export function trackResponse(method: string | undefined, url: string | undefined, requestBody: unknown, data: unknown): void {
  const funnel = funnelEventFor(method, url)
  if (funnel) track(funnel)
  if (!method || !url) return
  const m = method.toUpperCase()
  const path = pathOf(url)
  const role = sink?.role()
  for (const rule of CATALOG_RESPONSES) {
    if (rule.method !== m) continue
    if (rule.roles && (!role || !rule.roles.includes(role))) continue
    const match = path.match(rule.path)
    if (!match) continue
    const context = { match, body: parseBody(requestBody), data }
    if (rule.when && !rule.when(context)) continue
    if (rule.paidOnce) {
      const key = rule.paidOnce(context)
      if (paidTransactions.has(key)) continue
      paidTransactions.add(key)
    }
    send(rule.name, { props: rule.props?.(context) })
  }
}
